import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Share, X, PlusSquare } from "lucide-react";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export default function InstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState("generic"); // generic | ios

  useEffect(() => {
    if (isStandalone() || localStorage.getItem("ava_install_dismissed") === "1") return;

    if (isIOS()) {
      setPlatform("ios");
      const t = setTimeout(() => setVisible(true), 2500);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
      setPlatform("generic");
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem("ava_install_dismissed", "1");
  };

  const handleInstall = async () => {
    if (!deferredEvent) return;
    deferredEvent.prompt();
    await deferredEvent.userChoice;
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[95] w-[92%] max-w-sm"
          data-testid="install-ava-banner"
        >
          <div className="glass-panel rounded-2xl px-4 py-3.5 flex items-start gap-3 border border-cyan-400/20">
            <div className="w-8 h-8 rounded-lg bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Download className="w-4 h-4 text-cyan-300" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-display font-semibold text-white">Install AVA on this device</p>
              {platform === "ios" ? (
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                  Tap <Share className="w-3 h-3 inline -mt-0.5" /> Share, then{" "}
                  <PlusSquare className="w-3 h-3 inline -mt-0.5" /> "Add to Home Screen" for a full-screen app experience.
                </p>
              ) : (
                <p className="text-[11px] text-slate-400 mt-1">Full-screen, offline-ready, no App Store needed.</p>
              )}
              {platform === "generic" && (
                <button
                  onClick={handleInstall}
                  data-testid="install-ava-confirm-button"
                  className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25 border border-cyan-400/30 text-[10px] font-mono-data uppercase tracking-wider transition-colors"
                >
                  <Download className="w-3 h-3" /> Install
                </button>
              )}
            </div>
            <button
              onClick={dismiss}
              data-testid="install-ava-dismiss-button"
              className="text-slate-500 hover:text-white transition-colors flex-shrink-0"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
